import type { FlattenedSectionProps } from ".";
import { makeFullAddress } from "../../../features/questionnaire/utils/makeFullAddress";
import type {
  AddressModes,
  Answers,
  Groups,
} from "../../../features/questionnaire";
import { isPercentKindGroup } from "../../../features/questionnaire";
import type { Locale } from "../../../types";
import { formatDateByLocale } from "../../../utils";
import type { Options } from "./Question/QuestionFactory";
import type { Answer, QuestionProps } from "./Question/types";

export type GroupByAssumptionParams = {
  questions: QuestionProps[];
  answers: Answers;
  sections: FlattenedSectionProps[];
  addressModes?: AddressModes;
  locale?: Locale;
  groups?: Groups;
};

type MakeContentParams = {
  question: QuestionProps;
  answers: Answers;
  sections: FlattenedSectionProps[];
  addressModes?: AddressModes;
  locale?: Locale;
};

type MakePercentGroupContentParams = {
  question: QuestionProps;
  answers: Answers;
  questions: QuestionProps[];
  groups: Groups;
};

// If the question has options, lookup the label
const getAnswerText = (answer: Answer, options?: Options): string => {
  if (Array.isArray(answer)) {
    return answer.join(", ");
  }
  return (
    options?.find(o => o.value === answer)?.label ||
    (typeof answer === "object" ? "" : answer?.toString())
  );
};

const makeContent = ({
  question,
  answers,
  sections,
  addressModes,
  locale,
}: MakeContentParams) => {
  const { id, name, presentationText, options, hasSensitiveData, display } =
    question;
  const { type } = display;
  const isAddress = type === "address_lookup";
  const isDate = type === "date";
  let answer: string;

  // If the question is an address lookup, and its address mode is "manual",
  // then derive makeFullAddress
  if (isAddress && addressModes?.[name] === "manual") {
    answer = makeFullAddress(name, { sections, answers });
  } else if (isDate && answers[name]) {
    answer = formatDateByLocale(answers[name] as string, locale);
  } else {
    answer = getAnswerText(answers[name], options);
  }
  return {
    id,
    name,
    text: presentationText || "",
    answer,
    hasSensitiveData,
    readOnly: isAddress,
  };
};

export const makePercentGroupContent = ({
  question,
  answers,
  questions,
  groups,
}: MakePercentGroupContentParams) => {
  const { id, name, presentationText, hasSensitiveData } = question;
  const memberNames = groups[name].names;

  // Find all member questions and filter out readonly ones and zero percentages
  const memberAnswers = memberNames
    .map(memberName => {
      const memberQuestion = questions.find(q => q.name === memberName);
      const memberAnswer = answers[memberName];

      // Skip if readonly (this is the "total" question) or answer is 0% (design decision to omit these from display)
      if (memberQuestion?.readOnly || memberAnswer === "0%") {
        return null;
      }

      return memberQuestion?.primaryText && memberAnswer
        ? `${memberQuestion.primaryText} ${String(memberAnswer)}`
        : null;
    })
    .filter(Boolean);

  const EM_SPACE = "\u2003";
  const answer = memberAnswers.join(EM_SPACE);

  const presentationTextFallback = "Breakdown <br/>";

  return {
    id,
    name,
    text: presentationText || presentationTextFallback,
    answer,
    hasSensitiveData,
    readOnly: false,
  };
};

export const groupByAssumption = ({
  questions,
  answers,
  sections,
  addressModes,
  locale,
  groups,
}: GroupByAssumptionParams) => {
  const groupedQuestions = questions.reduce<Record<string, QuestionProps[]>>(
    (acc, question) => {
      const assumptionGroup = question.presentationGroup || "";

      // Filter and group questions that are not visible
      // NOTE: address_lookup questions should ALWAYS be visible
      if (
        (question.visible || question.display.type === "address_lookup") &&
        assumptionGroup !== ""
      ) {
        acc[assumptionGroup] = acc[assumptionGroup] || [];
        acc[assumptionGroup].push(question);
      }
      return acc;
    },
    {},
  );

  return Object.entries(groupedQuestions).map(
    ([assumptionGroup, groupQuestions]) => ({
      header: assumptionGroup,
      content: groupQuestions.map(question => {
        /* Check if this is a percent group shadow question
          This is called here instead of in makeContent to avoid growing the signature of makeContent
          and growing the conditional logic within it. It might be worth revisiting makeContent later 
          to see if it can be refactored further. */
        if (groups && isPercentKindGroup(groups[question.name])) {
          return makePercentGroupContent({
            question,
            answers,
            questions,
            groups,
          });
        }

        return makeContent({
          question,
          answers,
          addressModes,
          sections,
          locale,
        });
      }),
      help: groupQuestions
        .filter(({ helpInfo }) => helpInfo)
        .map(
          ({ helpInfo }) =>
            `<h3>${helpInfo?.headline}</h3>
${helpInfo?.text}`,
        )
        .join("\n"),
    }),
  );
};
